import type { FormField } from '../../../commons/PageForm';
import { toDateInputValue } from '../proveedor/proveedor-config-preview';

export interface ServicioAsignadoCrearFormValues {
  proveedorId: string;
  categoriaServicioId: string;
  fechaInicio: string;
  seFacturaAId: string;
  precio: string;
  cortesia: string;
  comentarios: string;
  inactivo: string;
}

export function makeEmptyServicioAsignadoForm(
  defaultSeFacturaAId = '',
): ServicioAsignadoCrearFormValues {
  return {
    proveedorId: '',
    categoriaServicioId: '',
    fechaInicio: '',
    seFacturaAId: defaultSeFacturaAId,
    precio: '',
    cortesia: 'false',
    comentarios: '',
    inactivo: 'false',
  };
}

export function isInactivoActiva(form: ServicioAsignadoCrearFormValues): boolean {
  return form.inactivo === 'true';
}

export const SERVICIO_ASIGNADO_INACTIVO_TEXT_STYLE = {
  color: '#94a3b8',
} as const;

export function isServicioAsignadoRowInactivo(row: unknown): boolean {
  if (row == null || typeof row !== 'object') return false;
  const r = row as Record<string, unknown>;
  if (r.inactivo === true || r.inactivo === 'true') return true;
  if (r.inactivo === false || r.inactivo === 'false') return false;
  return r.fechaFin != null && String(r.fechaFin).trim() !== '';
}

export function isCortesiaActiva(form: ServicioAsignadoCrearFormValues): boolean {
  return form.cortesia === 'true';
}

export function prefillServicioAsignadoForm(
  data: unknown,
  prev: ServicioAsignadoCrearFormValues,
): ServicioAsignadoCrearFormValues {
  if (data == null || typeof data !== 'object') return prev;
  const row = data as Record<string, unknown>;
  const next = { ...prev };

  const fecha = row.fechaInicio ?? row.seraInicio;
  if (fecha != null && String(fecha).trim() !== '') {
    next.fechaInicio = toDateInputValue(fecha);
  }

  const cortesiaApi = row.cortesia === true || row.cortesia === 'true';
  next.cortesia = cortesiaApi ? 'true' : 'false';
  if (cortesiaApi) {
    next.precio = '0';
  } else if (row.precio != null && row.precio !== '') {
    next.precio = String(row.precio);
  } else if (row.ultimoPrecio != null && row.ultimoPrecio !== '') {
    next.precio = String(row.ultimoPrecio);
  }

  if (row.comentarios != null && String(row.comentarios).trim() !== '') {
    next.comentarios = String(row.comentarios).trim();
  }

  const seFacturaA = row.seFacturaA;
  if (seFacturaA != null && typeof seFacturaA === 'object' && !Array.isArray(seFacturaA)) {
    const id = (seFacturaA as { id?: unknown }).id;
    if (id != null) next.seFacturaAId = String(id);
  } else if (row.seFacturaAId != null) {
    next.seFacturaAId = String(row.seFacturaAId);
  }

  const proveedor = row.proveedor;
  if (proveedor != null && typeof proveedor === 'object' && !Array.isArray(proveedor)) {
    const id = (proveedor as { id?: unknown }).id;
    if (id != null) next.proveedorId = String(id);
  } else if (row.proveedorId != null) {
    next.proveedorId = String(row.proveedorId);
  }

  const categoria = row.categoriaServicio;
  if (categoria != null && typeof categoria === 'object' && !Array.isArray(categoria)) {
    const id = (categoria as { id?: unknown }).id;
    if (id != null) next.categoriaServicioId = String(id);
  } else if (row.categoriaServicioId != null) {
    next.categoriaServicioId = String(row.categoriaServicioId);
  } else if (row.idCategoriaServicio != null) {
    next.categoriaServicioId = String(row.idCategoriaServicio);
  }

  next.inactivo = isServicioAsignadoRowInactivo(row) ? 'true' : 'false';

  return next;
}

export function applyServicioAsignadoFieldChange(
  prev: ServicioAsignadoCrearFormValues,
  name: keyof ServicioAsignadoCrearFormValues,
  value: string,
): ServicioAsignadoCrearFormValues {
  if (name === 'cortesia') {
    const cortesia = value === 'true';
    return {
      ...prev,
      cortesia: value,
      precio: cortesia ? '0' : prev.precio,
    };
  }
  if (name === 'precio' && isCortesiaActiva(prev)) return prev;
  return { ...prev, [name]: value };
}

export function validateServicioAsignadoForm(
  form: ServicioAsignadoCrearFormValues,
  opts?: { requireCategoria?: boolean; servicioCargado?: boolean },
): Partial<Record<keyof ServicioAsignadoCrearFormValues, string>> {
  const errs: Partial<Record<keyof ServicioAsignadoCrearFormValues, string>> = {};
  if (!form.proveedorId.trim()) errs.proveedorId = 'Seleccione un proveedor';
  if (opts?.requireCategoria !== false && !form.categoriaServicioId.trim()) {
    errs.categoriaServicioId = 'Seleccione una categoría de servicio';
  }
  if (opts?.servicioCargado === false) {
    errs.categoriaServicioId =
      errs.categoriaServicioId ??
      'Espere a que se cargue el servicio o seleccione otra categoría';
  }
  if (!form.fechaInicio.trim()) errs.fechaInicio = 'La fecha de inicio es requerida';
  if (!form.seFacturaAId.trim()) errs.seFacturaAId = 'Seleccione la empresa a facturar';
  if (!isCortesiaActiva(form)) {
    const precioNum = Number(form.precio);
    if (form.precio.trim() === '' || !Number.isFinite(precioNum)) {
      errs.precio = 'Ingrese un precio válido';
    }
  }
  return errs;
}

export function buildServicioAsignadoSubmitBody(
  form: ServicioAsignadoCrearFormValues,
): Record<string, unknown> {
  const cortesia = isCortesiaActiva(form);
  const precioNum = cortesia ? 0 : Number(form.precio);
  const comentarios = form.comentarios.trim();
  return {
    categoriaServicio: { id: Number(form.categoriaServicioId) },
    proveedor: { id: Number(form.proveedorId) },
    fechaInicio: form.fechaInicio.trim(),
    seFacturaA: { id: Number(form.seFacturaAId) },
    precio: precioNum,
    cortesia,
    comentarios: comentarios === '' ? null : comentarios,
    inactivo: isInactivoActiva(form),
  };
}

export type ServicioAsignadoBaseFieldsOpts = {
  proveedorOptions: { value: string; label: string }[];
  empresaOptions: { value: string; label: string }[];
  proveedorBloqueado: boolean;
  cortesiaActiva: boolean;
  categoriaField: FormField<ServicioAsignadoCrearFormValues>;
};

export function buildServicioAsignadoBaseFields(
  opts: ServicioAsignadoBaseFieldsOpts,
): FormField<ServicioAsignadoCrearFormValues>[] {
  return [
    {
      name: 'proveedorId',
      label: 'Proveedor',
      type: 'select',
      required: true,
      options: opts.proveedorOptions,
      disabled: opts.proveedorBloqueado,
    },
    opts.categoriaField,
    {
      name: 'fechaInicio',
      label: 'Fecha inicio',
      type: 'date',
      required: true,
    },
    {
      name: 'seFacturaAId',
      label: 'Se factura a',
      type: 'select',
      required: true,
      options: opts.empresaOptions,
    },
    {
      name: 'cortesia',
      label: 'Cortesía',
      type: 'checkbox',
    },
    {
      name: 'precio',
      label: 'Precio',
      type: 'number',
      required: !opts.cortesiaActiva,
      placeholder: '0.00',
      disabled: opts.cortesiaActiva,
    },
    {
      name: 'comentarios',
      label: 'Comentarios',
      type: 'textarea',
      rows: 3,
      placeholder: 'Comentarios opcionales',
    },
  ];
}

/** Id del servicio asignado en respuesta GET (p. ej. 376). */
export function idServicioFromApiResponse(data: unknown): number | null {
  if (data == null || typeof data !== 'object') return null;
  const id = (data as Record<string, unknown>).id;
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

export function proveedorBloqueadoPorServicioApi(data: unknown): boolean {
  return idServicioFromApiResponse(data) != null;
}

export type ServicioHistoricoEntry = {
  fecha: string;
  precio: number;
};

/** Histórico de precios del GET, ordenado por fecha (más reciente primero). */
export function parseServicioHistorico(data: unknown): ServicioHistoricoEntry[] {
  if (data == null || typeof data !== 'object') return [];
  const body = data as Record<string, unknown>;
  const historico = body.historico;
  if (!Array.isArray(historico)) return [];

  const precioIdRaw = body.precioId;
  const precioId =
    precioIdRaw != null && precioIdRaw !== '' && Number.isFinite(Number(precioIdRaw))
      ? Number(precioIdRaw)
      : null;

  const items: ServicioHistoricoEntry[] = [];
  for (const row of historico) {
    if (row == null || typeof row !== 'object') continue;
    const r = row as { id?: unknown; fecha?: unknown; precio?: unknown };
    const rowId = r.id != null && r.id !== '' ? Number(r.id) : NaN;
    if (precioId != null && Number.isFinite(rowId) && rowId === precioId) continue;
    const fecha = r.fecha != null ? String(r.fecha).trim() : '';
    const precio = Number(r.precio);
    if (!fecha || !Number.isFinite(precio)) continue;
    items.push({ fecha, precio });
  }

  return items.sort((a, b) => {
    const ta = Date.parse(a.fecha);
    const tb = Date.parse(b.fecha);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
    return b.fecha.localeCompare(a.fecha);
  });
}

export function idServicioFromServicioPreciosRow(row: any): number | null {
  const id = row?.idServicio ?? row?.id_servicio;
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

/** Id de categoría (alta: GET …/servicio/{categoriaServicioId} para cargar borrador). */
export function categoriaServicioIdFromServicioPreciosRow(row: any): number | null {
  const candidates = [
    row?.categoriaServicio?.id,
    row?.categoriaServicioId,
    row?.idCategoriaServicio,
    row?.catServicioId,
  ];
  for (const c of candidates) {
    if (c == null || c === '') continue;
    const n = Number(c);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function categoriaServicioDescripcionFromApi(data: unknown): string {
  if (data == null || typeof data !== 'object') return '';
  const row = data as Record<string, unknown>;
  const cat = row.categoriaServicio;
  if (cat != null && typeof cat === 'object' && !Array.isArray(cat)) {
    const c = cat as { descripcion?: unknown; alias?: unknown };
    const descripcion = c.descripcion != null ? String(c.descripcion).trim() : '';
    if (descripcion) return descripcion;
    const alias = c.alias != null ? String(c.alias).trim() : '';
    if (alias) return alias;
  }
  if (row.servDescripcion != null && String(row.servDescripcion).trim() !== '') {
    return String(row.servDescripcion).trim();
  }
  return '';
}

export function categoriaServicioReadOnlyField(): FormField<ServicioAsignadoCrearFormValues> {
  return {
    name: 'categoriaServicioId',
    label: 'Categoría servicio',
    type: 'text',
    readOnly: true,
  };
}

export function servicioAsignadoDisplayValues(
  form: ServicioAsignadoCrearFormValues,
  categoriaLabel: string,
): ServicioAsignadoCrearFormValues {
  return {
    ...form,
    categoriaServicioId: categoriaLabel.trim() || form.categoriaServicioId,
  };
}

export function categoriaServicioLabelFromRow(row: any): string {
  const fromApi = categoriaServicioDescripcionFromApi(row);
  if (fromApi) return fromApi;
  const id = categoriaServicioIdFromServicioPreciosRow(row);
  return id != null ? `ID ${id}` : '—';
}
